import { useEffect } from "react";
import { Link } from "react-router";
import { ThemeToggle } from "@mieweb/ui";
import { BrandLockup } from "../components/BrandMark";
import {
  changelog,
  CHANGELOG_STORAGE_KEY,
  LATEST_CHANGELOG_VERSION,
} from "../data/changelog";

function TagBadge({ type }: { type: "feature" | "improvement" | "fix" }) {
  const styles = {
    feature:
      "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
    improvement:
      "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
    fix: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  };
  const labels = { feature: "New", improvement: "Improved", fix: "Fixed" };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[type]}`}
    >
      {labels[type]}
    </span>
  );
}

export function ChangelogPage() {
  // Mark the latest changelog version as seen when visiting this page
  useEffect(() => {
    localStorage.setItem(CHANGELOG_STORAGE_KEY, LATEST_CHANGELOG_VERSION);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 dark:from-neutral-950 to-white dark:to-neutral-900">
      {/* Navigation */}
      <nav className="bg-white dark:bg-neutral-900 border-b border-slate-200 dark:border-neutral-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <BrandLockup
                iconClassName="h-7 w-7"
                textClassName="text-xl font-bold text-slate-900 dark:text-white"
              />
            </Link>
            <div className="flex items-center gap-3">
              <ThemeToggle mode="two-way" size="sm" variant="ghost" />
              <Link
                to="/docs"
                className="text-slate-600 dark:text-neutral-400 hover:text-slate-900 dark:hover:text-white transition-colors text-sm"
              >
                Docs
              </Link>
              <Link
                to="/login"
                className="text-slate-600 dark:text-neutral-400 hover:text-slate-900 dark:hover:text-white transition-colors text-sm"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-3">
          Changelog
        </h1>
        <p className="text-lg text-slate-500 dark:text-neutral-400 max-w-2xl">
          New features, improvements, and fixes shipped to Pollenate. Follow
          along as we build the best feedback platform for developers.
        </p>
      </header>

      {/* Timeline */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[7px] top-2 bottom-0 w-px bg-slate-200 dark:bg-neutral-800 hidden sm:block" />

          <div className="space-y-16">
            {changelog.map((entry, idx) => (
              <article key={entry.version} className="relative">
                {/* Timeline dot */}
                <div
                  className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border-[3px] border-white dark:border-neutral-900 hidden sm:block"
                  style={{
                    background:
                      idx === 0
                        ? "linear-gradient(135deg, #f59e0b, #f97316)"
                        : "rgb(203 213 225)",
                  }}
                />

                <div className="sm:pl-10">
                  {/* Date & version */}
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <time
                      className="text-sm font-medium text-slate-400 dark:text-neutral-500"
                      dateTime={entry.date}
                    >
                      {entry.date}
                    </time>
                    <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-100 dark:bg-neutral-800 text-slate-500 dark:text-neutral-400">
                      v{entry.version}
                    </span>
                  </div>

                  {/* Title */}
                  <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2">
                    {entry.title}
                  </h2>

                  {/* Description */}
                  {entry.description && (
                    <p className="text-slate-600 dark:text-neutral-400 mb-6 leading-relaxed">
                      {entry.description}
                    </p>
                  )}

                  {/* Changes */}
                  <div className="space-y-4">
                    {entry.features && entry.features.length > 0 && (
                      <div className="space-y-2">
                        {entry.features.map((item, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <TagBadge type="feature" />
                            <span className="text-slate-700 dark:text-neutral-300 text-sm leading-relaxed">
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {entry.improvements && entry.improvements.length > 0 && (
                      <div className="space-y-2">
                        {entry.improvements.map((item, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <TagBadge type="improvement" />
                            <span className="text-slate-700 dark:text-neutral-300 text-sm leading-relaxed">
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {entry.fixes && entry.fixes.length > 0 && (
                      <div className="space-y-2">
                        {entry.fixes.map((item, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <TagBadge type="fix" />
                            <span className="text-slate-700 dark:text-neutral-300 text-sm leading-relaxed">
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-neutral-800 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-400 dark:text-neutral-500">
            &copy; 2026 Pollenate by{" "}
            <a
              href="https://bluehive.com?utm_source=pollenate&utm_medium=website&utm_campaign=footer_copyright"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-600 dark:hover:text-neutral-300 transition-colors"
            >
              BlueHive Health
            </a>
            . All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm">
            <Link
              to="/vs/qualtrics"
              className="text-slate-400 dark:text-neutral-500 hover:text-slate-600 dark:hover:text-neutral-300 transition-colors"
            >
              vs Qualtrics
            </Link>
            <Link
              to="/vs/google-forms"
              className="text-slate-400 dark:text-neutral-500 hover:text-slate-600 dark:hover:text-neutral-300 transition-colors"
            >
              vs Google Forms
            </Link>
            <Link
              to="/vs/typeform"
              className="text-slate-400 dark:text-neutral-500 hover:text-slate-600 dark:hover:text-neutral-300 transition-colors"
            >
              vs Typeform
            </Link>
            <Link
              to="/docs"
              className="text-slate-400 dark:text-neutral-500 hover:text-slate-600 dark:hover:text-neutral-300 transition-colors"
            >
              Docs
            </Link>
            <Link
              to="/privacy"
              className="text-slate-400 dark:text-neutral-500 hover:text-slate-600 dark:hover:text-neutral-300 transition-colors"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              className="text-slate-400 dark:text-neutral-500 hover:text-slate-600 dark:hover:text-neutral-300 transition-colors"
            >
              Terms
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
